import React, { useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions, 
    Button, 
    TextField, 
    Alert,
    CircularProgress
} from '@mui/material';
import { 
    EmailAuthProvider, 
    reauthenticateWithCredential, 
    reauthenticateWithPopup, 
    AuthProvider,
    GoogleAuthProvider,
    OAuthProvider
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useTranslation } from 'react-i18next';

interface ReauthModalProps {
    open: boolean;
    onClose: (success: boolean) => void;
}

export const ReauthModal: React.FC<ReauthModalProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const user = auth.currentUser;
    // Determine the primary provider (default to password if none found)
    const providerId = user?.providerData[0]?.providerId || 'password';

    const handleReauth = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);

        try {
            if (providerId === 'password') {
                const credential = EmailAuthProvider.credential(user.email!, password);
                await reauthenticateWithCredential(user, credential);
                onClose(true);
            } else {
                // Handle Federated Providers (Google & Microsoft)
                let provider: AuthProvider;
                if (providerId === 'google.com') {
                    provider = new GoogleAuthProvider();
                } else if (providerId === 'microsoft.com') {
                    provider = new OAuthProvider('microsoft.com');
                    const tenantId = import.meta.env.VITE_MS_TENANT_ID;
                    if (tenantId) {
                        (provider as OAuthProvider).setCustomParameters({
                            tenant: tenantId,
                            prompt: 'select_account'
                        });
                    }
                } else {
                    throw new Error(t('auth.unsupportedProvider'));
                }

                // Unified Popup Flow for both providers
                // This keeps the user on the page and preserves the Promise chain in SudoContext
                await reauthenticateWithPopup(user, provider);
                onClose(true);
            }
        } catch (err: any) {
            console.error("Re-authentication failed:", err);
            // Handle popup closed by user specifically
            if (err.code === 'auth/popup-closed-by-user') {
                // Do nothing, user just cancelled. 
                // Don't close the modal, let them try again or click cancel.
            } else {
                setError(t('auth.reauthFailed', { message: err.message }));
            }
        } finally {
            setLoading(false);
            setPassword('');
        }
    };

    const handleCancel = () => {
        setError(null);
        setPassword('');
        onClose(false);
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{t('auth.securityCheck', 'Security Check')}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t('auth.reauthPrompt', 'To access this protected area, please confirm your identity.')}
                </DialogContentText>
                
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {providerId === 'password' ? (
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('auth.password', 'Password')}
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleReauth();
                            }
                        }}
                    />
                ) : (
                    <Button 
                        variant="contained" 
                        fullWidth 
                        onClick={handleReauth}
                        disabled={loading}
                        sx={{ mt: 1, py: 1.5 }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.signInWith', { provider: providerId === 'microsoft.com' ? 'Microsoft' : 'Google' })}
                    </Button>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCancel} disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                {providerId === 'password' && (
                    <Button 
                        onClick={handleReauth} 
                        variant="contained" 
                        color="primary" 
                        disabled={loading || !password}
                    >
                        {loading ? <CircularProgress size={24} /> : t('common.confirm', 'Confirm')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
